import { useState, useCallback, useMemo, useRef } from 'react';

// File type interface
interface FileType {
  path: string;
  name: string;
  size: string;
}

/**
 * Custom hook for managing content form state (Text and AI Summary)
 * Handles text content, AI summary, and file uploads
 */
export const useContentForm = () => {
  // Text content specific state
  const [textType, setTextType] = useState<'long' | 'short'>('long');
  const [textTitle, setTextTitle] = useState<string>('');
  const [textContent, setTextContent] = useState<string>('');
  const [textFiles, setTextFiles] = useState<FileType[]>([]);

  // AI Summary specific state
  const [agent, setAgent] = useState<'chatGPT' | 'deepSeek'>('chatGPT');
  const [aiPrompt, setAiPrompt] = useState<string>('');
  const [aiFiles, setAiFiles] = useState<FileType[]>([]);

  // Track upload completion status for both file lists
  const [isTextFilesReady, setIsTextFilesReady] = useState<boolean>(true);
  const [isAiFilesReady, setIsAiFilesReady] = useState<boolean>(true);

  // Use refs to prevent infinite callback loops
  const lastTextFilesRef = useRef<string>('');
  const lastAiFilesRef = useRef<string>('');

  // Memoize TinyEditor callback to prevent infinite re-renders
  const handleTextContentChange = useCallback((htmlContent: string) => {
    setTextContent(htmlContent);
  }, []);

  // Memoize recommended characters to prevent re-renders
  const recommendedCharacters = useMemo(() => {
    return textType === 'long' ? 165 : 85;
  }, [textType]);

  // Memoize preview files to prevent infinite re-renders
  const previewFiles = useMemo(() => {
    return textFiles?.map(doc => ({
      name: doc.name,
      url: doc.path
    })) || [];
  }, [textFiles]);

  // Memoize file upload callbacks to prevent infinite loops
  const handleTextFilesChange = useCallback((files: any[]) => {
    console.log('handleTextFilesChange called with:', files);
    // Include both path AND name in the key since name can change independently
    const filesKey = files.map(f => `${f.path}|${f.name}`).join(',');
    if (filesKey !== lastTextFilesRef.current) {
      console.log('✅ Updating textFiles state with:', files);
      lastTextFilesRef.current = filesKey;
      setTextFiles(files);
    }
  }, []);

  const handleAiFilesChange = useCallback((files: any[]) => {
    // Include both path AND name in the key since name can change independently
    const filesKey = files.map(f => `${f.path}|${f.name}`).join(',');
    if (filesKey !== lastAiFilesRef.current) {
      lastAiFilesRef.current = filesKey;
      setAiFiles(files);
    }
  }, []);

  // Callbacks for text and AI file upload completion (To disable the confirm button while uploading)
  const handleTextFilesIsAllow = useCallback((isAllowed: boolean) => {
    setIsTextFilesReady(isAllowed);
  }, []);

  const handleAiFilesIsAllow = useCallback((isAllowed: boolean) => {
    setIsAiFilesReady(isAllowed);
  }, []);

  // Memoize defaultList to prevent UploadFileList from detecting changes
  // Include both path and name in dependency since name can change independently
  const memoizedTextFiles = useMemo(
    () => textFiles,
    [textFiles.length, textFiles.map(f => `${f.path}|${f.name}`).join(',')]
  );
  const memoizedAiFiles = useMemo(
    () => aiFiles,
    [aiFiles.length, aiFiles.map(f => `${f.path}|${f.name}`).join(',')]
  );

  // Reset all form fields to default values
  const resetFormFields = useCallback(() => {
    // Text Page
    setTextType('long');
    setTextTitle('');
    setTextContent('');
    setTextFiles([]);
    // AI Page
    setAiPrompt('');
    setAgent('chatGPT');
    setAiFiles([]);
  }, []);

  return {
    // Text content state
    textType,
    setTextType,
    textTitle,
    setTextTitle,
    textContent,
    setTextContent,
    textFiles,
    setTextFiles,
    
    // AI Summary state
    agent,
    setAgent,
    aiPrompt,
    setAiPrompt,
    aiFiles,
    setAiFiles,
    
    // Upload status
    isTextFilesReady,
    isAiFilesReady,
    
    // Computed values
    recommendedCharacters,
    previewFiles,
    memoizedTextFiles,
    memoizedAiFiles,
    
    // Callbacks
    handleTextContentChange,
    handleTextFilesChange,
    handleAiFilesChange,
    handleTextFilesIsAllow,
    handleAiFilesIsAllow,
    resetFormFields,
  };
};
